<template>
    <div class="w-full @container h-full" :class="isDemo && 'pointer-events-none'">
        <div class="rounded flex flex-col bg-white p-5 box-border h-full flex-1 flex-grow min-h-[588px]">
            <div class="flex flex-row flex-wrap lg:flex-nowrap justify-between items-center mb-4">
                <p class="text-body-3 text-default-1 font-medium xxs:p-5 lg:p-0">Inbox</p>
                <router-link :to="{name: 'inbox'}" class="flex flex-row items-center cursor-pointer" v-if="hasEmailsOrSMSes">
                    <p class="text-body-6 font-medium text-primary-1 mr-1.5">View all</p>
                    <ArrowRight :color="color.hex"/>
                </router-link>
            </div>
            <div class="flex">
                <button
                    v-for="tab in tabs"
                    :key="tab.name"
                    @click="selected = tab"
                    :class="[
                        selected.name === tab.name ? 'border-b-[2px] border-primary-1 text-primary-1' : 'border-b-[2px] border-primary-4 text-default-2',
                        'bg-white border-interactive-9 text-body-6 font-medium py-1.5 text-center w-1/2',
                    ]">
                    {{ tab.name }}
                </button>
            </div>

            <div v-if="selected.name === 'SMS'" class="overflow-hidden mt-4 flex-1 flex flex-col">
                <div v-if="loading" class="overflow-y-scroll no-scrollbar border-box px-1">
                    <div class="w-full h-[70px] skeleton-loader mt-1" v-for="i in 4" :key="i"></div>
                </div>
                <div v-if="smses.length === 0 && !loading" class="flex flex-col items-center flex-1 justify-center self-center">
                    <CommunicationsIcon :color="color.hex" class="mb-6"/>
                    <p class="text-body-5 text-default-1">There are no new SMSes</p>
                </div>
                <div v-else class="flex flex-col flex-1 overflow-y-auto no-scrollbar">
                    <div
                        v-for="sms in smses" :key="sms.uid"
                    >
                        <SMSItem size="large" :item="sms" layout="dashboard"
                                 @click="handleSMSClick(sms)"/>
                    </div>
                </div>
            </div>
            <div v-else class="overflow-hidden mt-4 flex-1 flex flex-col">
                <div v-if="loading" class="overflow-y-scroll no-scrollbar border-box px-1">
                    <div class="w-full h-[70px] skeleton-loader mt-1" v-for="i in 4" :key="i"></div>
                </div>
                <div v-if="emails.length === 0 && !loading" class="flex flex-col items-center flex-1 flex-grow justify-center">
                    <CommunicationsIcon :color="color.hex" class="mb-6"/>
                    <p class="text-body-5 text-default-1">There are no new emails</p>
                </div>
                <div v-else class="flex flex-col flex-1 overflow-y-auto no-scrollbar">
                    <div
                        v-for="email in emails"
                        :key="email.uid"
                    >
                        <EmailItem size="large" :item="email" layout="dashboard" @click="handleEmailClick(email)" />
                    </div>
                </div>
            </div>
            <div v-if="selected.name === 'SMS'" class="flex flex-row mt-4">
                <CoreButton v-if="isAdmin || isContentAdmin" buttonType="primary" label="Create SMS" class="w-full"
                            @click="router.push('/sms/create')"/>
            </div>
            <div v-else class="flex xxs:flex-col lg:flex-row xxs:mt-6 lg:mt-4">
                <CoreButton v-if="isAdmin || isContentAdmin" buttonType="primary" label="Create Email" class="w-full xxs:mt-4 lg:mt-0" @click="router.push('/email/create')"/>
            </div>
        </div>
    </div>
</template>
<script setup>
import {computed, onMounted, ref} from 'vue';
import CommunicationsIcon from '../icons/Communications.vue';
import SMSItem from "@/views/admin/sms/SMSItem.vue";
import EmailItem from "@/views/admin/email/Item.vue";
const {getEmailInbox} = useEmailInbox();
import CoreButton from "@/components/core/Button.vue";
import {useRouter} from "vue-router";
import {useEmailInbox} from "@/composables/useEmailInbox";
import {useAuth} from "@/stores/AuthStore";
import {useSMSInbox} from "@/composables/useSMSInbox";
import ArrowRight from "@/components/icons/navigation/ArrowRight.vue";
const {getSMSInbox} = useSMSInbox();
import {useTheme} from "@/stores/ThemeStore";
import {useWidgetDemoData} from "@/composables/useWidgetDemoData";

const {inboxDemoData: demoData} = useWidgetDemoData();

const props = defineProps({
    isDemo: {
        type: Boolean,
        default: false
    }
});

const {color} = useTheme();

const {isAdmin, isContentAdmin} = useAuth();
const router = useRouter();

const tabs = [ {name: 'Emails'},  {name: 'SMS'}];
const selected = ref(tabs[0]);

const {organization} = useAuth();

const {updateEmail} = useEmailInbox();
const {updateSMS} = useSMSInbox();

const smsesData = ref([]);
const emailsData = ref([]);
const loading = ref(false);

async function handleEmailClick(email) {
    if(email.read_at === null) {
        await updateEmail(organization.slug, email.uid, {}).catch(() => {});
    }

    const item = 'email' in email ?  email : {email: email};

    await router.push({name: 'inbox', params: {activeSection: 'email', activeItem: JSON.stringify(item)}})
}

async function handleSMSClick(SMS) {
    if(SMS.read_at === null) {
        await updateSMS(organization.slug, SMS.uid, {});
    }

    const item = 'sms' in SMS ? SMS : {sms: SMS};

    await router.push({ name: 'inbox', params: { activeSection: 'sms', activeItem: JSON.stringify(item) } });
}

const hasEmailsOrSMSes = computed(() => {
    return emails.value.length > 0 || smses.value.length > 0;
})

const smses = computed(() => {
    return props.isDemo ? demoData : smsesData.value;
})

const emails = computed(() => {
    return props.isDemo ? demoData : emailsData.value;
})

onMounted(async () => {
    if(props.isDemo) {
        return;
    }

    loading.value = true;

    await getSMSInbox(organization.slug, 1, 5).then((response) => {
        smsesData.value = response.data;
    });

    await getEmailInbox(organization.slug, 1, 5).then((response) => {
        emailsData.value = response.data;
    });

    loading.value = false;
})
</script>